import { ZuruflexApiError } from '@zuruflex/api-client';
import { emptyTermsDoc, isTermsDocEmpty } from '@zuruflex/core';
import type { PartnerTerms, TermsDoc, TermsStatus } from '@zuruflex/types';
import { Alert, Badge, Button, Card, PageHeader, TermsDocument } from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TermsEditor } from '../components/TermsEditor.js';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';

const STATUS_VARIANT: Record<TermsStatus, 'default' | 'info' | 'success' | 'warning'> = {
  DRAFT: 'default',
  IN_REVIEW: 'info',
  PUBLISHED: 'success',
  REJECTED: 'warning',
};

/**
 * Where a partner writes the conditions their renters agree to.
 *
 * The page is deliberately one screen rather than a list plus an editor:
 * there is only ever one working copy, and the published history is
 * something you look at rather than something you navigate.
 */
export function TermsPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const { user } = usePartner();
  const queryClient = useQueryClient();

  const state = useQuery({ queryKey: ['terms'], queryFn: () => api.myTerms() });
  const working = state.data?.working ?? null;
  const current = state.data?.current ?? null;

  // Only an owner commits the business to what it is promising a renter,
  // which is the same reason asking to be reviewed is owner-only.
  const isOwner = user.role === 'OWNER';
  const canEdit = user.role === 'OWNER' || user.role === 'MANAGER';
  const editable = canEdit && working?.status !== 'IN_REVIEW';

  const [doc, setDoc] = useState<TermsDoc>(emptyTermsDoc);
  /*
   * Which stored row the editor is showing. Null until the query answers,
   * and 'blank' for a partner who has never written anything — the editor
   * needs a value that changes exactly once in that case, so it seeds
   * itself and then leaves the typing alone.
   */
  const [documentKey, setDocumentKey] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ tone: 'ok' | 'bad'; text: string } | null>(null);
  /*
   * What the server last accepted. Compared on blur so that tabbing out of
   * an untouched editor is not a write — the page is opened far more often
   * to read the published version than to change the draft.
   */
  const saved = useRef<string | null>(null);

  useEffect(() => {
    if (!state.data) return;
    const source = state.data.working ?? state.data.current ?? null;
    const starting = source?.doc ?? emptyTermsDoc();
    setDoc(starting);
    setDocumentKey(source?.id ?? 'blank');
    saved.current = JSON.stringify(starting);
  }, [state.data]);

  const save = useMutation({
    mutationFn: (next: TermsDoc) => api.saveMyTermsDraft({ doc: next }),
    onSuccess: async (row) => {
      saved.current = JSON.stringify(row.doc);
      setFeedback({ tone: 'ok', text: t('common:state.saved') });
      await queryClient.invalidateQueries({ queryKey: ['terms'] });
    },
    onError: (cause) => setFeedback({ tone: 'bad', text: describe(cause, t) }),
  });

  const submit = useMutation({
    mutationFn: () => api.submitMyTerms(),
    onSuccess: async () => {
      setFeedback({ tone: 'ok', text: t('terms.submitted') });
      await queryClient.invalidateQueries({ queryKey: ['terms'] });
    },
    onError: (cause) => setFeedback({ tone: 'bad', text: describe(cause, t) }),
  });

  const saveIfChanged = () => {
    if (!editable) return;
    const next = JSON.stringify(doc);
    if (next === saved.current) return;
    save.mutate(doc);
  };

  const status = working?.status ?? (current ? 'PUBLISHED' : null);

  return (
    <>
      <PageHeader
        title={t('terms.title')}
        description={t('terms.lead')}
        action={
          status ? <Badge variant={STATUS_VARIANT[status]}>{t(`terms.status.${status}`)}</Badge> : null
        }
      />

      <div className="flex max-w-3xl flex-col gap-6">
        {/* A partner with nothing published is not in an error state. */}
        {!current ? <Alert variant="info">{t('terms.noneYet')}</Alert> : null}

        {working?.status === 'REJECTED' && working.reviewNote ? (
          <Alert variant="warning" title={t('terms.sentBack')}>
            {working.reviewNote}
          </Alert>
        ) : null}

        {working?.status === 'IN_REVIEW' ? <Alert variant="info">{t('terms.withZuruflex')}</Alert> : null}

        {feedback ? (
          <Alert variant={feedback.tone === 'ok' ? 'success' : 'danger'}>{feedback.text}</Alert>
        ) : null}

        <TermsEditor
          doc={doc}
          documentKey={documentKey}
          editable={Boolean(editable)}
          onChange={setDoc}
          onBlur={saveIfChanged}
        />

        {canEdit ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              disabled={!editable}
              isLoading={save.isPending}
              onClick={saveIfChanged}
            >
              {t('terms.save')}
            </Button>
            {isOwner ? (
              <Button
                type="button"
                disabled={!editable || isTermsDocEmpty(doc)}
                isLoading={submit.isPending}
                onClick={() => {
                  // Whatever is on screen is what goes to Zuruflex, so an
                  // unsaved edit is written before the version is frozen.
                  saveIfChanged();
                  submit.mutate();
                }}
              >
                {t('terms.submit')}
              </Button>
            ) : (
              <p className="text-xs text-text-muted">{t('terms.ownerSubmits')}</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-text-muted">{t('terms.readOnly')}</p>
        )}

        {state.data?.history.length ? (
          <section className="mt-6 flex flex-col gap-4">
            <h2 className="text-sm font-bold text-text-default">{t('terms.history')}</h2>
            <p className="text-xs text-text-muted">{t('terms.historyHint')}</p>
            {state.data.history.map((version) => (
              <PublishedVersion key={version.id} version={version} locale={i18n.language} />
            ))}
          </section>
        ) : null}
      </div>
    </>
  );
}

/**
 * A published version, read-only and collapsed.
 *
 * Kept on the page rather than behind a route because the question it
 * answers — "what did the hire I am arguing about actually say" — is asked
 * while looking at this screen, and a version that needs navigating to is
 * one nobody checks.
 */
function PublishedVersion({ version, locale }: { version: PartnerTerms; locale: string }) {
  const { t } = useTranslation('partner');
  const published = version.publishedAt ? new Date(version.publishedAt) : null;

  return (
    <Card className="p-4">
      <details>
        <summary className="cursor-pointer text-sm font-semibold text-text-default">
          {t('terms.version', { version: version.version })}
          {published ? (
            <span className="ml-2 font-normal text-text-muted">
              {published.toLocaleDateString(locale, { dateStyle: 'medium' })}
            </span>
          ) : null}
        </summary>
        <div className="mt-4 border-t border-border-default pt-4">
          <TermsDocument doc={version.doc} />
        </div>
      </details>
    </Card>
  );
}

function describe(cause: unknown, t: (key: string) => string): string {
  return cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected');
}
